/**
 * EF-AI-CREW-ONBOARDING-FRONTEND-V1: AI Crew Onboarding Review Panel
 * EF-AI-CREW-ONBOARDING-STABILISATION-V1: Full error traceability
 * 
 * A dedicated admin interface for reviewing AI-extracted crew data from documents.
 * Features:
 * - Split-screen layout: Documents (left) | Extracted data form (right)
 * - Field-level confidence scores with color coding
 * - Conflict resolution UI
 * - Warnings panel for missing/low-confidence fields
 * - Smart Error Panel with expandable details and "Copy Error Report" button
 * - No auto-save - admin must explicitly approve
 */

import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// ONB-FIX-005: Configure pdf.js worker (matches the bundled pdfjs-dist version).
// Using unpkg CDN keeps CRA build config untouched (no webpack worker plumbing).
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
import api, { extractErrorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { ScrollArea } from "../components/ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Progress } from "../components/ui/progress";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Upload,
  FileText,
  Image as ImageIcon,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  XCircle,
  RefreshCw,
  UserPlus,
  UserCircle,
  Eye,
  Trash2,
  ChevronRight,
  ChevronDown,
  Info,
  Sparkles,
  FileWarning,
  ArrowRight,
  Check,
  X,
  Copy,
  Bug,
} from "lucide-react";

/**
 * EF-AI-CREW-ONBOARDING-STABILISATION-V1:
 * Structured error object from backend.
 * {
 *   success: false,
 *   step: "STEP_NAME",
 *   step_name: "Human Readable Step Name",
 *   error_type: "TYPE",
 *   message: "user message",
 *   debug: "exact technical cause",
 *   file_name: "...",
 *   trace_id: "..."
 * }
 */

/**
 * EF-AI-CREW-ONBOARDING-STABILISATION-V1:
 * Parse and extract structured error from API response.
 */
const parseStructuredError = (error) => {
  // Check if error response contains the standardized format
  const data = error?.response?.data;
  
  // Case 1: Direct standardized error (JSON response with success: false)
  if (data && data.success === false) {
    return {
      isStructured: true,
      step: data.step || "UNKNOWN",
      stepName: data.step_name || "Unknown Step",
      errorType: data.error_type || "Error",
      message: data.message || "An error occurred",
      debug: data.debug || null,
      fileName: data.file_name || null,
      traceId: data.trace_id || null,
      timestamp: data.timestamp || new Date().toISOString(),
    };
  }
  
  // Case 2: HTTPException with detail object
  const detail = data?.detail;
  if (detail && typeof detail === "object" && detail.success === false) {
    return {
      isStructured: true,
      step: detail.step || "UNKNOWN",
      stepName: detail.step_name || "Unknown Step",
      errorType: detail.error_type || "Error",
      message: detail.message || "An error occurred",
      debug: detail.debug || null,
      fileName: detail.file_name || null,
      traceId: detail.trace_id || null,
      timestamp: detail.timestamp || new Date().toISOString(),
    };
  }
  
  // Case 3: Legacy error format - try to extract what we can
  if (detail && typeof detail === "object") {
    return {
      isStructured: false,
      step: null,
      stepName: null,
      errorType: detail.error_type || "Error",
      message: detail.error || detail.message || "An error occurred",
      debug: detail.debug || null,
      fileName: null,
      traceId: null,
      timestamp: new Date().toISOString(),
    };
  }
  
  // Case 4: String detail
  if (detail && typeof detail === "string") {
    return {
      isStructured: false,
      step: null,
      stepName: null,
      errorType: "Error",
      message: detail,
      debug: null,
      fileName: null,
      traceId: null,
      timestamp: new Date().toISOString(),
    };
  }
  
  // Case 5: Network error or unknown
  return {
    isStructured: false,
    step: null,
    stepName: null,
    errorType: error?.name || "Error",
    message: error?.message || "An unknown error occurred",
    debug: null,
    fileName: null,
    traceId: null,
    timestamp: new Date().toISOString(),
  };
};

/**
 * EF-AI-CREW-ONBOARDING-STABILISATION-V1:
 * Get user-friendly message from structured error.
 */
const getUserFriendlyMessage = (structuredError) => {
  // If we have a user message from the backend, use it
  if (structuredError.message && !structuredError.message.includes("Exception")) {
    return structuredError.message;
  }
  
  // Map known error types to friendly messages
  const debug = (structuredError.debug || "").toLowerCase();
  const errorType = (structuredError.errorType || "").toLowerCase();
  
  // OCR/Text extraction issues
  if (debug.includes("insufficient text") || debug.includes("too short")) {
    return "We couldn't read enough text from this document. Please upload a clearer image or higher-quality scan.";
  }
  
  if (debug.includes("ocr") && debug.includes("fail")) {
    return "We had trouble reading this document. Try uploading a clearer image.";
  }
  
  // AI processing issues
  if (debug.includes("empty") && debug.includes("response")) {
    return "We couldn't extract information from this file. Please try a different document.";
  }
  
  if (debug.includes("json") && (debug.includes("parse") || debug.includes("decode"))) {
    return "We received an unexpected response. Please try again.";
  }
  
  // Network/timeout issues
  if (debug.includes("timeout") || debug.includes("timed out")) {
    return "The request took too long. Please try again with a smaller file.";
  }
  
  if (debug.includes("network") || debug.includes("connection")) {
    return "Connection issue. Please check your internet and try again.";
  }
  
  // File format issues
  if (debug.includes("unsupported") && debug.includes("format")) {
    return "This file format isn't supported. Please upload a PDF, JPG, or PNG.";
  }
  
  if (debug.includes("corrupted") || debug.includes("invalid file")) {
    return "This file appears to be damaged. Please try a different file.";
  }
  
  // AI key issues
  if (errorType.includes("auth") || debug.includes("api key") || debug.includes("unauthorized")) {
    return "AI service configuration error. Please contact support.";
  }
  
  // Fallback to the error message
  return structuredError.message || "Something went wrong while processing the document. Please try again.";
};

/**
 * EF-AI-CREW-ONBOARDING-STABILISATION-V1:
 * Smart Error Panel Component
 * Displays structured error with expandable details and copy functionality.
 */
const SmartErrorPanel = ({ error, onDismiss }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const userMessage = getUserFriendlyMessage(error);
  
  // Build error report for copying
  const buildErrorReport = () => {
    const lines = [
      "=== AI CREW ONBOARDING ERROR REPORT ===",
      "",
      `Timestamp: ${error.timestamp}`,
      `Trace ID: ${error.traceId || "N/A"}`,
      "",
      `Step: ${error.step || "N/A"} (${error.stepName || "Unknown"})`,
      `Error Type: ${error.errorType}`,
      `File: ${error.fileName || "N/A"}`,
      "",
      "User Message:",
      userMessage,
      "",
      "Technical Details:",
      error.debug || "No additional details",
      "",
      "=== END OF REPORT ==="
    ];
    return lines.join("\n");
  };
  
  const handleCopyReport = async () => {
    const report = buildErrorReport();
    try {
      await navigator.clipboard.writeText(report);
      setCopied(true);
      toast.success("Error report copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = report;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success("Error report copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  return (
    <Alert variant="destructive" className="relative" data-testid="smart-error-panel">
      <XCircle className="h-4 w-4" />
      <AlertTitle className="pr-8">Extraction Failed</AlertTitle>
      <AlertDescription>
        <p className="mb-3">{userMessage}</p>
        
        {/* Expandable details */}
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              data-testid="error-details-toggle"
            >
              {isExpanded ? (
                <ChevronDown className="h-3 w-3 mr-1" />
              ) : (
                <ChevronRight className="h-3 w-3 mr-1" />
              )}
              {isExpanded ? "Hide Details" : "Show Details"}
            </Button>
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <div className="mt-3 p-3 bg-red-950/30 rounded-lg text-xs space-y-2 font-mono" data-testid="error-details">
              {error.traceId && (
                <div className="flex justify-between">
                  <span className="text-red-300">Trace ID:</span>
                  <span className="text-red-100">{error.traceId}</span>
                </div>
              )}
              {error.step && (
                <div className="flex justify-between">
                  <span className="text-red-300">Failed Step:</span>
                  <span className="text-red-100">{error.step}</span>
                </div>
              )}
              {error.stepName && (
                <div className="flex justify-between">
                  <span className="text-red-300">Step Name:</span>
                  <span className="text-red-100">{error.stepName}</span>
                </div>
              )}
              {error.fileName && (
                <div className="flex justify-between">
                  <span className="text-red-300">File:</span>
                  <span className="text-red-100 truncate max-w-[200px]">{error.fileName}</span>
                </div>
              )}
              {error.errorType && (
                <div className="flex justify-between">
                  <span className="text-red-300">Error Type:</span>
                  <span className="text-red-100">{error.errorType}</span>
                </div>
              )}
              {error.debug && (
                <div className="mt-2">
                  <span className="text-red-300">Debug:</span>
                  <p className="text-red-100 mt-1 break-words whitespace-pre-wrap">{error.debug}</p>
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
        
        {/* Actions */}
        <div className="flex items-center gap-2 mt-3">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs bg-transparent border-red-400/50 hover:bg-red-900/30"
            onClick={handleCopyReport}
            data-testid="copy-error-report-btn"
          >
            {copied ? (
              <Check className="h-3 w-3 mr-1" />
            ) : (
              <Copy className="h-3 w-3 mr-1" />
            )}
            {copied ? "Copied!" : "Copy Error Report"}
          </Button>
          
          {onDismiss && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={onDismiss}
              data-testid="dismiss-error-btn"
            >
              Dismiss
            </Button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
};

// Confidence level helpers
const getConfidenceColor = (confidence) => {
  if (confidence >= 0.85) return "text-green-600 dark:text-green-400";
  if (confidence >= 0.6) return "text-yellow-600 dark:text-yellow-400";
  return "text-red-600 dark:text-red-400";
};

const getConfidenceBg = (confidence) => {
  if (confidence >= 0.85) return "bg-green-100 dark:bg-green-900/30 border-green-200 dark:border-green-800";
  if (confidence >= 0.6) return "bg-yellow-100 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800";
  return "bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-800";
};

const getConfidenceLabel = (confidence) => {
  if (confidence >= 0.85) return "High";
  if (confidence >= 0.6) return "Medium";
  return "Low";
};

const getConfidenceIcon = (confidence) => {
  if (confidence >= 0.85) return <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />;
  if (confidence >= 0.6) return <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />;
  return <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />;
};

// EVENTSFLO-CREW-ONBOARDING-BANK-SOURCE-ATTRIBUTION-FIX-V1:
// Backend now always tries to attach the originating filename to every
// ExtractedField. This helper is the last line of defence: when a stray
// legacy extraction still arrives with null / "" / the literal string
// "None", we render a stable, human label instead of leaking it to the UI.
const formatSource = (raw) => {
  if (raw === null || raw === undefined) return "Unknown source";
  const s = String(raw).trim();
  if (!s || s.toLowerCase() === "none") return "Unknown source";
  return s;
};

// Field display component with confidence
const ExtractedField = ({ 
  label, 
  field, 
  value, 
  onChange, 
  conflict,
  onResolveConflict 
}) => {
  const confidence = field?.confidence ?? 0;
  const source = formatSource(field?.source);
  const hasSource = field && field.source !== undefined && field.source !== null && String(field.source).trim() !== "" && String(field.source).toLowerCase() !== "none";
  // EVENTSFLO-CREW-ONBOARDING-RIB-NAME-FALLBACK-FRONTEND-V1:
  // The backend now exposes a human-readable ``source_label`` whenever a
  // value was not extracted directly from a higher-priority document
  // (e.g. names inferred from a RIB account holder). Render the label as a
  // small amber chip so the admin sees that the value is unverified.
  const sourceLabel = field?.source_label;
  const hasConflict = conflict !== undefined;
  
  return (
    <div className={`p-3 rounded-lg border ${hasConflict ? 'border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20' : getConfidenceBg(confidence)}`}>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <Label className="text-sm font-medium">{label}</Label>
        <div className="flex items-center gap-2 flex-wrap">
          {sourceLabel && (
            <span
              data-testid={`field-source-label-${label.toLowerCase().replace(/\s+/g, '-')}`}
              title={`Inferred from: ${sourceLabel}. Please verify against an official document.`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-400/60 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[10px] font-medium leading-none"
            >
              <AlertTriangle className="h-2.5 w-2.5" />
              {sourceLabel}
            </span>
          )}
          {field && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1">
                    {getConfidenceIcon(confidence)}
                    <span className={`text-xs font-medium ${getConfidenceColor(confidence)}`}>
                      {(confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Confidence: {getConfidenceLabel(confidence)}</p>
                  <p className="text-xs text-muted-foreground">Source: {source}</p>
                  {sourceLabel && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Inferred from: {sourceLabel}
                    </p>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
      
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="bg-white dark:bg-gray-900"
        placeholder={`Enter ${label.toLowerCase()}`}
      />
      
      {hasSource && (
        <p className="text-xs text-muted-foreground mt-1">
          Source: {source}
        </p>
      )}
      
      {/* Conflict UI */}
      {hasConflict && (
        <div className="mt-2 p-2 bg-orange-100 dark:bg-orange-900/30 rounded border border-orange-200 dark:border-orange-800">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            <span className="text-xs font-medium text-orange-700 dark:text-orange-300">Conflict Detected</span>
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Check className="h-3 w-3 text-green-600" />
              <span className="text-xs">Chosen: <strong>{conflict.chosen_value}</strong></span>
            </div>
            {conflict.alternatives?.map((alt, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <X className="h-3 w-3 text-gray-400" />
                <span className="text-xs text-muted-foreground">Alternative: {alt}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 text-xs px-2"
                  onClick={() => onResolveConflict(alt)}
                >
                  Use this
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * EVENTSFLO-CREW-ONBOARDING-TXT-RTF-VIEWER-PREVIEW-V1
 *
 * Inline preview for TXT / RTF attachments in the onboarding viewer.
 *
 * - TXT: read as UTF-8 (fallback ISO-8859-1 if non-UTF-8 bytes).
 * - RTF: strip control words / groups via a small static helper (mirrors the
 *   backend `_strip_rtf`); preserves newlines so the AI's extracted content
 *   is recognisable to a human reviewer.
 * - Fetches the blob via the `preview_url` (`blob:` URL created by
 *   ingestFiles / ingestInboxAttachments). No new network call, no new
 *   endpoint, no impact on extraction or persistence — these files remain
 *   `extract_only` and never reach `/api/staff/{id}/documents`.
 * - Long files are capped at 200 KB of preview text (still tons of context
 *   for a human reviewer) to keep the panel snappy.
 */
const TXT_PREVIEW_MAX_CHARS = 200_000;

function stripRtfClient(rtf) {
  if (!rtf) return "";
  let text = rtf;
  // \'XX hex escape → original char (interpret as latin-1)
  text = text.replace(/\\'([0-9a-fA-F]{2})/g, (_, hex) => {
    try {
      return String.fromCharCode(parseInt(hex, 16));
    } catch {
      return "";
    }
  });
  // \uNNNN? unicode escape → corresponding char (skip the trailing fallback char if any)
  text = text.replace(/\\u(-?\d+)\??/g, (_, n) => String.fromCharCode(((parseInt(n, 10) % 65536) + 65536) % 65536));
  // RTF control words: \word, \word123, optionally with a trailing space
  text = text.replace(/\\[a-zA-Z]+-?\d*\s?/g, "");
  // Escaped specials (\\ \{ \} \* etc.)
  text = text.replace(/\\[^a-zA-Z]/g, "");
  // Drop group braces but keep newlines/text
  text = text.replace(/[{}]/g, "");
  // Collapse runs of spaces/tabs; keep newlines
  text = text.replace(/[ \t]+/g, " ").replace(/\n{3,}/g, "\n\n");
  return text.trim();
}

const TextPreview = ({ doc }) => {
  const [text, setText] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setText(null);
    setError(null);
    setLoading(true);

    const url = doc?.preview_url;
    if (!url) {
      setError("No preview URL available.");
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const blob = await fetch(url).then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.blob();
        });
        // UTF-8 first, ISO-8859-1 fallback (matches backend behaviour).
        let raw;
        try {
          raw = await blob.text();
        } catch {
          const buf = await blob.arrayBuffer();
          raw = new TextDecoder("iso-8859-1", { fatal: false }).decode(buf);
        }

        const mime = (doc.mime_type || "").toLowerCase();
        const filename = (doc.filename || "").toLowerCase();
        const isRtf =
          mime === "application/rtf" ||
          mime === "text/rtf" ||
          filename.endsWith(".rtf") ||
          (raw && raw.trimStart().startsWith("{\\rtf"));

        const out = isRtf ? stripRtfClient(raw) : raw;
        const capped = out && out.length > TXT_PREVIEW_MAX_CHARS
          ? out.slice(0, TXT_PREVIEW_MAX_CHARS) + "\n\n…[preview truncated — full content still feeds AI extraction]"
          : (out || "");

        if (!cancelled) {
          setText(capped);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Failed to read file.");
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [doc?.preview_url, doc?.mime_type, doc?.filename]);

  if (loading) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground" data-testid="document-preview-text-loading">
        Loading preview…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6 text-center" data-testid="document-preview-text-error">
        <FileWarning className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm">Preview unavailable — {error}</p>
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => {
            const link = document.createElement("a");
            link.href = doc.preview_url;
            link.download = doc.filename;
            link.click();
          }}
        >
          Download File
        </Button>
      </div>
    );
  }
  if (!text) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground" data-testid="document-preview-text-empty">
        File is empty.
      </div>
    );
  }
  return (
    <pre
      data-testid="document-preview-text"
      className="p-4 text-xs leading-relaxed whitespace-pre-wrap break-words font-mono text-foreground/90 max-h-[70vh] overflow-auto"
    >
      {text}
    </pre>
  );
};

/**
 * ONB-FIX-005: PDF.js-based PDF preview.
 * Renders one page at a time via react-pdf (canvas, no native plugin needed).
 * Falls back to "Open in new tab" if rendering errors out.
 */
const PdfPreview = ({ src, fileName }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [loadError, setLoadError] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const containerRef = useRef(null);
  const panStateRef = useRef({ dragging: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 });

  // Stable options object — react-pdf re-renders when this reference changes
  const options = useMemo(
    () => ({
      cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
      cMapPacked: true,
    }),
    []
  );

  const onLoadSuccess = useCallback(({ numPages: n }) => {
    setNumPages(n);
    setPageNumber(1);
    setLoadError(null);
  }, []);

  const onLoadError = useCallback((err) => {
    console.error("[PdfPreview] load error:", err);
    setLoadError(err?.message || "Failed to load PDF");
  }, []);

  // Measure container width for responsive page sizing
  const measureRef = useCallback((node) => {
    containerRef.current = node;
    if (node) {
      setContainerWidth(node.clientWidth);
    }
  }, []);

  // EVENTSFLO-ADD-PDF-PAN-AFTER-ZOOM-V1: click-and-drag panning when zoomed
  const handlePanStart = useCallback((e) => {
    const node = containerRef.current;
    if (!node) return;
    // only start panning when there's actually overflow to pan
    const canPan = node.scrollWidth > node.clientWidth || node.scrollHeight > node.clientHeight;
    if (!canPan) return;
    panStateRef.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      scrollLeft: node.scrollLeft,
      scrollTop: node.scrollTop,
    };
    setIsPanning(true);
  }, []);

  const handlePanMove = useCallback((e) => {
    const state = panStateRef.current;
    const node = containerRef.current;
    if (!state.dragging || !node) return;
    e.preventDefault();
    node.scrollLeft = state.scrollLeft - (e.clientX - state.startX);
    node.scrollTop = state.scrollTop - (e.clientY - state.startY);
  }, []);

  const handlePanEnd = useCallback(() => {
    if (panStateRef.current.dragging) {
      panStateRef.current.dragging = false;
      setIsPanning(false);
    }
  }, []);

  return (
    <div className="w-full" data-testid="document-preview-pdf">
      <div
        ref={measureRef}
        className="w-full bg-muted/20 overflow-auto"
        style={{
          height: "70vh",
          minHeight: "500px",
          cursor: isPanning ? "grabbing" : (scale > 1 ? "grab" : "default"),
        }}
        onMouseDown={handlePanStart}
        onMouseMove={handlePanMove}
        onMouseUp={handlePanEnd}
        onMouseLeave={handlePanEnd}
      >
        {loadError ? (
          <div className="p-8 text-center max-w-md">
            <FileWarning className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm font-medium mb-1">Could not render PDF inline</p>
            <p className="text-xs text-muted-foreground mb-3">{loadError}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(src, "_blank", "noopener,noreferrer")}
              data-testid="document-preview-pdf-open-newtab"
            >
              <Eye className="h-4 w-4 mr-1" />
              Open in new tab
            </Button>
          </div>
        ) : (
          <div
            className="mx-auto select-none"
            style={{ width: "fit-content", padding: "8px" }}
            // disable native drag/text selection while panning interactions are active
            onDragStart={(e) => e.preventDefault()}
          >
            <Document
              file={src}
              onLoadSuccess={onLoadSuccess}
              onLoadError={onLoadError}
              options={options}
              loading={
                <div className="p-8 flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading PDF…
                </div>
              }
            >
              <Page
                pageNumber={pageNumber}
                width={containerWidth ? Math.max(containerWidth - 16, 320) * scale : undefined}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                data-testid={`document-preview-pdf-page-${pageNumber}`}
              />
            </Document>
          </div>
        )}
      </div>

      {/* Toolbar: pagination + zoom + filename + open in new tab */}
      <div className="p-3 border-t bg-muted/30 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            disabled={!numPages || pageNumber <= 1}
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            data-testid="document-preview-pdf-prev"
          >
            Prev
          </Button>
          <span className="text-xs text-muted-foreground px-2 min-w-[80px] text-center">
            {numPages ? `${pageNumber} / ${numPages}` : "—"}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={!numPages || pageNumber >= numPages}
            onClick={() => setPageNumber((p) => Math.min(numPages || 1, p + 1))}
            data-testid="document-preview-pdf-next"
          >
            Next
          </Button>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setScale((s) => Math.max(0.5, +(s - 0.25).toFixed(2)))}
            disabled={!numPages}
            data-testid="document-preview-pdf-zoom-out"
          >
            −
          </Button>
          <span className="text-xs text-muted-foreground px-2 min-w-[48px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setScale((s) => Math.min(3, +(s + 0.25).toFixed(2)))}
            disabled={!numPages}
            data-testid="document-preview-pdf-zoom-in"
          >
            +
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground truncate max-w-[160px]">
            {fileName}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(src, "_blank", "noopener,noreferrer")}
            data-testid="document-open-newtab-btn"
          >
            <Eye className="h-4 w-4 mr-1" />
            Open in new tab
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const link = document.createElement("a");
              link.href = src;
              link.download = fileName;
              link.click();
            }}
            data-testid="document-download-btn"
          >
            <FileText className="h-4 w-4 mr-1" />
            Download
          </Button>
        </div>
      </div>
    </div>
  );
};


// Document card component
const DocumentCard = ({ doc, isSelected, onSelect, onRemove }) => {
  const isImage = doc.mime_type?.startsWith("image/");
  const isPdf = doc.mime_type === "application/pdf";
  
  return (
    <div
      className={`p-3 rounded-lg border cursor-pointer transition-all ${
        isSelected 
          ? "border-primary bg-primary/5 ring-2 ring-primary/20" 
          : "border-border hover:border-primary/50"
      }`}
      onClick={onSelect}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded ${isImage ? "bg-blue-100 dark:bg-blue-900/30" : "bg-gray-100 dark:bg-gray-800"}`}>
          {isImage ? (
            <ImageIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          ) : (
            <FileText className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{doc.filename}</p>
          <p className="text-xs text-muted-foreground">
            {doc.document_type_hint || (isImage ? "Image" : isPdf ? "PDF" : "Document")}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// Warnings panel component
const WarningsPanel = ({ warnings, missingFields, lowConfidenceFields, conflicts }) => {
  const hasIssues = warnings?.length > 0 || missingFields?.length > 0 || lowConfidenceFields?.length > 0 || conflicts?.length > 0;
  
  if (!hasIssues) {
    return (
      <Alert className="border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800">
        <CheckCircle2 className="h-4 w-4 text-green-600" />
        <AlertTitle className="text-green-700 dark:text-green-300">All checks passed</AlertTitle>
        <AlertDescription className="text-green-600 dark:text-green-400">
          No issues detected. Ready to create crew profile.
        </AlertDescription>
      </Alert>
    );
  }
  
  return (
    <div className="space-y-3">
      {missingFields?.length > 0 && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Missing Required Fields</AlertTitle>
          <AlertDescription>
            {missingFields.join(", ")}
          </AlertDescription>
        </Alert>
      )}
      
      {conflicts?.length > 0 && (
        <Alert className="border-orange-200 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-800">
          <AlertTriangle className="h-4 w-4 text-orange-600" />
          <AlertTitle className="text-orange-700 dark:text-orange-300">Conflicts Detected</AlertTitle>
          <AlertDescription className="text-orange-600 dark:text-orange-400">
            {conflicts.length} field(s) have conflicting values. Review and resolve below.
          </AlertDescription>
        </Alert>
      )}
      
      {lowConfidenceFields?.length > 0 && (
        <Alert className="border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertTitle className="text-yellow-700 dark:text-yellow-300">Low Confidence Fields</AlertTitle>
          <AlertDescription className="text-yellow-600 dark:text-yellow-400">
            {lowConfidenceFields.join(", ")} - please verify
          </AlertDescription>
        </Alert>
      )}
      
      {/* EVENTSFLO-CREW-ONBOARDING-RIB-NAME-FALLBACK-FRONTEND-V1:
          Amber alert for warnings flagging that an Identity field was
          inferred from a non-identity source (e.g. a RIB). Pinned to the
          top so admins see it before reviewing the Identity section. */}
      {warnings?.filter(w =>
        w?.includes("inferred from bank account holder")
        || w?.includes("Joint bank account holder")
        || w?.includes("Bank account holder looks like a company")
      ).map((warning, idx) => (
        <Alert
          key={`rib-warn-${idx}`}
          data-testid="rib-name-fallback-warning"
          className="border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700"
        >
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-700 dark:text-amber-300">Identity not verified</AlertTitle>
          <AlertDescription className="text-amber-700 dark:text-amber-300">
            {warning}
          </AlertDescription>
        </Alert>
      ))}

      {warnings?.filter(w =>
        !w?.includes("Missing")
        && !w?.includes("Low confidence")
        && !w?.includes("Conflict")
        && !w?.includes("inferred from bank account holder")
        && !w?.includes("Joint bank account holder")
        && !w?.includes("Bank account holder looks like a company")
      ).map((warning, idx) => (
        <Alert key={idx} className="border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-600 dark:text-blue-400">
            {warning}
          </AlertDescription>
        </Alert>
      ))}
    </div>
  );
};

export default function AICrewOnboardingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  
  // State
  const [documents, setDocuments] = useState([]);
  const [selectedDocIndex, setSelectedDocIndex] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [extractionResult, setExtractionResult] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  
  // EF-AI-CREW-ONBOARDING-STABILISATION-V1: Structured error state
  const [extractionError, setExtractionError] = useState(null);
  const [validationError, setValidationError] = useState(null);
  // ONB-FIX-008: Persistent on-page error for Create Crew Profile failures.
  const [createError, setCreateError] = useState(null);
  // EVENTSFLO-CREW-ID-PHOTO-DETECTION-AND-INBOX-DOWNLOAD-FIX-V1
  // Admin opt-in for using the cropped face crop as the staff profile photo.
  const [useIdPhotoAsProfile, setUseIdPhotoAsProfile] = useState(false);
  // EVENTSFLO-CREW-INBOX-ONBOARDING-DOCUMENT-PERSISTENCE-HEALTH-EMAIL-FALLBACK-V1
  // Inbox-source context captured from navigation state. Stored in refs so it
  // survives the navigate(replace:true, state:{}) call that clears
  // location.state to prevent re-ingestion on remount.
  const inboxSenderEmailRef = useRef(location.state?.inboxSenderEmail || null);
  const inboxMessageIdRef = useRef(location.state?.inboxMessageId || null);
  const inboxSenderEmail = inboxSenderEmailRef.current;
  const inboxMessageId = inboxMessageIdRef.current;

  // EVENTSFLO-CREW-ID-PHOTO-DETECTION-FAILURE-UX-V1
  // Allow admin to dismiss the "ID photo not detected" notice (Continue
  // without photo). Reset when a new extraction is run.
  const [idPhotoNoticeDismissed, setIdPhotoNoticeDismissed] = useState(false);
  
  // Form state (editable fields)
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    date_of_birth: "",
    // EVENTSFLO-CREW-PLACE-OF-BIRTH-PROFILE-AND-AI-ONBOARDING-V1
    place_of_birth: "",
    nationality: "",
    id_type: "",
    id_number: "",
    expiry_date: "",
    phone: "",
    email: "",
    // EVENTSFLO-CREW-INBOX-ONBOARDING-DOCUMENT-PERSISTENCE-HEALTH-EMAIL-FALLBACK-V1
    // Track where the email came from so we can render "Email inferred from sender" UX.
    email_source: "",
    address: "",
    // EVENTSFLO-CREW-ONBOARDING-ADDRESS-PROOF-AND-FRENCH-DOC-MAPPING-V1
    // Full address breakdown so city/postcode/line2 can be reviewed and
    // persisted to StaffAddress.line2 / .city / .postcode.
    address_line_2: "",
    address_city: "",
    address_postcode: "",
    // ONB-FIX-011: Health insurance card data (separate from id_*)
    health_card_type: "",
    health_card_number: "",
    // EVENTSFLO-CREW-ONBOARDING-BANK-DETAILS-FRONTEND-V1
    bank_account_holder: "",
    bank_name: "",
    bank_iban: "",
    bank_bic_swift: "",
    bank_rib_key: "",
  });

  // ONB-FIX-012: Existing-profile detection state (set when create returns 409)
  const [existingProfileMatch, setExistingProfileMatch] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // ONB-FIX-002: Shared file ingestion logic - used by BOTH manual selection and drag-drop
  // EVENTSFLO-CREW-ONBOARDING-TXT-RTF-TEXT-EXTRACTION-V1:
  //   .txt and .rtf attachments are accepted for AI extraction (email / phone /
  //   name hints) but are tagged `extract_only: true` so the upload-to-profile
  //   loop NEVER persists them as crew_documents. Existing supported types
  //   (PDF / images) continue to flow through unchanged.
  const ingestFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    const isTextLike = (t, n) =>
      t === "text/plain" ||
      t === "text/rtf" ||
      t === "application/rtf" ||
      n.endsWith(".txt") ||
      n.endsWith(".rtf");

    // Filter to supported types (defensive — drag-drop can include anything)
    const supported = files.filter((f) => {
      const t = (f.type || "").toLowerCase();
      const n = (f.name || "").toLowerCase();
      return (
        t === "application/pdf" ||
        t.startsWith("image/") ||
        n.endsWith(".pdf") ||
        n.endsWith(".jpg") ||
        n.endsWith(".jpeg") ||
        n.endsWith(".png") ||
        n.endsWith(".webp") ||
        isTextLike(t, n)
      );
    });

    if (supported.length === 0) {
      toast.error("Unsupported file type. Please upload PDF, JPG, PNG, WEBP, TXT, or RTF.");
      return;
    }

    setIsUploading(true);

    try {
      const newDocs = await Promise.all(
        supported.map(async (file) => {
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(",")[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          const t = (file.type || "").toLowerCase();
          const n = (file.name || "").toLowerCase();
          const extractOnly = isTextLike(t, n);

          return {
            filename: file.name,
            mime_type: file.type || (n.endsWith(".rtf") ? "application/rtf" : "application/octet-stream"),
            file_content_base64: base64,
            preview_url: URL.createObjectURL(file),
            document_type_hint: guessDocumentType(file.name),
            // EVENTSFLO-CREW-ONBOARDING-TXT-RTF-TEXT-EXTRACTION-V1
            extract_only: extractOnly,
          };
        })
      );

      setDocuments((prev) => [...prev, ...newDocs]);
      toast.success(`${supported.length} document(s) added`);
    } catch (error) {
      console.error("File read error:", error);
      toast.error("Failed to read files");
    } finally {
      setIsUploading(false);
    }
  }, []);

  // Handle file selection (manual via <input type="file">)
  const handleFileSelect = useCallback(
    async (e) => {
      const files = Array.from(e.target.files || []);
      await ingestFiles(files);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [ingestFiles]
  );

  // ONB-FIX-002: Drag-and-drop handlers — preventDefault stops the browser
  // from opening the dropped file in the tab. Files are routed through the
  // same ingestion logic as manual selection.
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    setIsDragging(true);
  }, []);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer?.files || []);
      await ingestFiles(files);
    },
    [ingestFiles]
  );
  
  // Guess document type from filename
  const guessDocumentType = (filename) => {
    const lower = filename.toLowerCase();
    // ONB-FIX-006: Carte Vitale must be classified separately from generic ID Card
    // (it is a French health-insurance card, not an identity document).
    if (lower.includes("vitale")) return "Carte Vitale";
    if (lower.includes("passport")) return "Passport";
    // EVENTSFLO-CREW-ONBOARDING-ADDRESS-PROOF-AND-FRENCH-DOC-MAPPING-V1
    // French + utility-bill filename hints feed normaliseHint later.
    if (/permis[-_ ]?(?:de[-_ ]?)?conduire|driving[-_ ]?licen[cs]e|driver(?:'s)?[-_ ]?licen[cs]e/.test(lower)) {
      return "Driver Licence";
    }
    if (/\bedf\b|justificatif|facture|domicile|attestation|utility|electricity|electricite/.test(lower)) {
      return "Proof of Address";
    }
    // EVENTSFLO-CREW-ONBOARDING-BANK-DETAILS-DOCUMENT-TYPE-V1
    if (/(?:^|[^a-z])(rib|iban|bic|swift|bank|statement)(?:[^a-z]|$)|coordonn[eé]es[-_ ]?bancaires|releve[-_ ]?d?[-_ ]?identite[-_ ]?bancaire/.test(lower)) {
      return "Bank Details";
    }
    // ONB-FIX-021: Distinguish ID Card front vs back so both sides are persisted as
    // separate document records (national_id_front / national_id_back) instead of
    // both landing on national_id_front and superseding each other.
    if (lower.includes("id") || lower.includes("carte")) {
      if (/\bback\b|verso|reverse/.test(lower)) return "ID Card Back";
      return "ID Card";
    }
    if (lower.includes("cv") || lower.includes("resume")) return "CV";
    if (lower.includes("permit") || lower.includes("visa")) return "Permit";
    if (lower.includes("cert") || lower.includes("diploma")) return "Certificate";
    return null;
  };

  // EVENTSFLO-INBOX-ATTACHMENT-TO-AI-ONBOARD-CREW-V1: ingest attachments handed off
  // from the Inbox page via navigation state. Fetches the file bytes from the
  // existing attachment endpoint and normalises into the same document shape used
  // by drag-drop / manual upload — so the rest of the AI onboarding pipeline is
  // entirely unchanged.
  const ingestInboxAttachments = useCallback(async (atts) => {
    if (!atts || atts.length === 0) return;
    setIsUploading(true);
    try {
      const token = localStorage.getItem("eventsflo_auth_token");
      const newDocs = await Promise.all(
        atts.map(async (att) => {
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/inbox/attachments/${att.attachment_id}/file`;
          const res = await fetch(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (!res.ok) {
            throw new Error(`Failed to fetch "${att.original_filename}" (${res.status})`);
          }
          const blob = await res.blob();
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(",")[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          return {
            filename: att.original_filename,
            mime_type: att.mime_type || blob.type || "application/octet-stream",
            file_content_base64: base64,
            preview_url: URL.createObjectURL(blob),
            document_type_hint: guessDocumentType(att.original_filename),
            // Source metadata preserved on the document object
            source: "inbox",
            source_message_id: att.message_id,
            source_attachment_id: att.attachment_id,
            // EVENTSFLO-CREW-ONBOARDING-TXT-RTF-TEXT-EXTRACTION-V1:
            // TXT / RTF attachments are extraction-only — they feed the AI
            // pipeline but MUST NOT be persisted to crew_documents.
            extract_only: (() => {
              const t = (att.mime_type || blob.type || "").toLowerCase();
              const n = (att.original_filename || "").toLowerCase();
              return (
                t === "text/plain" ||
                t === "text/rtf" ||
                t === "application/rtf" ||
                n.endsWith(".txt") ||
                n.endsWith(".rtf")
              );
            })(),
          };
        })
      );
      setDocuments((prev) => {
        // EVENTSFLO-FIX-DUPLICATE-INBOX-ONBOARD-ATTACHMENT-V1:
        // Defensive dedupe by source_attachment_id so a re-mounted effect
        // (StrictMode dev double-mount, browser back/forward, etc.) cannot
        // produce two rows for the same Inbox attachment.
        const seen = new Set(
          prev.map((d) => d?.source_attachment_id).filter(Boolean)
        );
        const filtered = newDocs.filter(
          (d) => !d.source_attachment_id || !seen.has(d.source_attachment_id)
        );
        return [...prev, ...filtered];
      });
      toast.success(`${newDocs.length} inbox attachment(s) loaded`);
    } catch (err) {
      console.error("[ONB] Inbox attachment ingest error:", err);
      toast.error(err.message || "Failed to load inbox attachment(s)");
    } finally {
      setIsUploading(false);
    }
  }, []);

  // Run once on mount if we arrived from Inbox with attachments.
  // EVENTSFLO-FIX-DUPLICATE-INBOX-ONBOARD-ATTACHMENT-V1:
  // React StrictMode (dev) intentionally double-invokes mount effects. The
  // empty-deps closure still holds `location.state.inboxAttachments` on the
  // second pass (the navigate-replace below is async), which previously
  // produced two ingest calls and two documents. A simple ref guard makes
  // ingestion idempotent regardless of how many times this effect fires.
  const didIngestInboxRef = useRef(false);
  useEffect(() => {
    if (didIngestInboxRef.current) return;
    const incoming = location.state?.inboxAttachments;
    const sender = location.state?.inboxSenderEmail;
    if (incoming && Array.isArray(incoming) && incoming.length > 0) {
      didIngestInboxRef.current = true;
      // EVENTSFLO-CREW-INBOX-ONBOARDING-DOCUMENT-PERSISTENCE-HEALTH-EMAIL-FALLBACK-V1
      // Pre-fill the email field from the inbox sender immediately so the
      // form is never blank when documents lack an email. The AI extraction
      // (run via the Extract button) will overwrite this only when it finds
      // a higher-confidence email and re-tag email_source accordingly.
      if (sender) {
        setFormData((prev) => ({
          ...prev,
          email: prev.email || sender,
          email_source: prev.email ? prev.email_source : "email_sender",
        }));
      }
      ingestInboxAttachments(incoming);
      // Clear the navigation state so re-renders / browser back don't re-ingest.
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Remove document
  const handleRemoveDocument = useCallback((index) => {
    setDocuments(prev => {
      const newDocs = [...prev];
      if (newDocs[index]?.preview_url) {
        URL.revokeObjectURL(newDocs[index].preview_url);
      }
      newDocs.splice(index, 1);
      return newDocs;
    });
    if (selectedDocIndex >= documents.length - 1) {
      setSelectedDocIndex(Math.max(0, selectedDocIndex - 1));
    }
  }, [selectedDocIndex, documents.length]);
  
  // Extract data from documents
  const handleExtract = useCallback(async () => {
    if (documents.length === 0) {
      toast.error("Please upload at least one document");
      return;
    }
    
    setIsExtracting(true);
    setExtractionResult(null);
    setValidationResult(null);
    setExtractionError(null); // Clear previous error
    // EVENTSFLO-CREW-ID-PHOTO-DETECTION-AND-INBOX-DOWNLOAD-FIX-V1: reset the
    // ID-photo opt-in whenever a new extraction starts so the toggle never
    // carries stale state from a previous upload session.
    setUseIdPhotoAsProfile(false);
    setIdPhotoNoticeDismissed(false);
    
    try {
      const response = await api.post(
        "/ai/crew/extract",
        {
          documents: documents.map(d => ({
            file_content_base64: d.file_content_base64,
            filename: d.filename,
            mime_type: d.mime_type,
            document_type_hint: d.document_type_hint,
          })),
          merge_strategy: "prefer_official",
        },
        { timeout: 180000 } // ONB-FIX-003: 3-min ceiling for OCR + AI extraction
      );
      
      // EF-AI-CREW-ONBOARDING-STABILISATION-V1: Check for structured error in response
      if (response.data && response.data.success === false) {
        // This is a structured error response
        const structuredError = parseStructuredError({ response: { data: response.data } });
        setExtractionError(structuredError);
        console.error("[AI_EXTRACT] Structured error:", structuredError);
        return;
      }
      
      if (response.data.success) {
        setExtractionResult(response.data);

        // Populate form with extracted data
        const draft = response.data.structured_draft;

        // ONB-FIX-009: Defensive guard — never let Carte Vitale / NIR / health-card values
        // populate the form's id_type / id_number / id_expiry_date fields, even if a stale
        // backend or older cached response tries to. National ID / Passport are the only
        // sources of identity-document data here.
        const idType = (draft.id_type?.value || "").toString();
        const looksLikeHealthCard = /vitale|nir|s[ée]curit[ée]\s*sociale|health[\s-]?card/i.test(idType);
        const safeIdType = looksLikeHealthCard ? "" : idType;
        const safeIdNumber = looksLikeHealthCard ? "" : (draft.id_number?.value || "");
        const safeExpiry = looksLikeHealthCard ? "" : (draft.expiry_date?.value || "");

        setFormData({
          first_name: draft.first_name?.value || "",
          last_name: draft.last_name?.value || "",
          date_of_birth: draft.date_of_birth?.value || "",
          // EVENTSFLO-CREW-PLACE-OF-BIRTH-PROFILE-AND-AI-ONBOARDING-V1
          place_of_birth: draft.place_of_birth?.value || "",
          nationality: draft.nationality?.value || "",
          id_type: safeIdType,
          id_number: safeIdNumber,
          expiry_date: safeExpiry,
          phone: draft.phone?.value || "",
          // EVENTSFLO-CREW-INBOX-ONBOARDING-DOCUMENT-PERSISTENCE-HEALTH-EMAIL-FALLBACK-V1
          // Email fallback: when the AI extraction returns no email, fall back
          // to the inbox sender (carried via location.state.inboxSenderEmail).
          // Higher-confidence extracted email always wins.
          email: draft.email?.value || inboxSenderEmail || "",
          email_source: draft.email?.value
            ? (draft.email?.source || "extracted_document")
            : (inboxSenderEmail ? "email_sender" : ""),
          address: draft.address?.value || "",
          // EVENTSFLO-CREW-ONBOARDING-ADDRESS-PROOF-AND-FRENCH-DOC-MAPPING-V1
          address_line_2: draft.address_line_2?.value || "",
          address_city: draft.address_city?.value || "",
          address_postcode: draft.address_postal_code?.value || "",
          // ONB-FIX-011: surface Carte Vitale data in its own fields
          health_card_type: draft.health_card_type?.value || "",
          health_card_number: draft.health_card_number?.value || "",
          // EVENTSFLO-CREW-ONBOARDING-BANK-DETAILS-FRONTEND-V1
          bank_account_holder: draft.bank_details?.account_holder?.value || "",
          bank_name:           draft.bank_details?.bank_name?.value || "",
          bank_iban:           draft.bank_details?.iban?.value || "",
          bank_bic_swift:      draft.bank_details?.bic_swift?.value || "",
          bank_rib_key:        draft.bank_details?.rib_key?.value || "",
        });
        
        toast.success("AI extraction complete - review the data below");
      } else {
        toast.error("Extraction failed");
      }
    } catch (error) {
      console.error("Extraction error:", error);
      
      // EF-AI-CREW-ONBOARDING-STABILISATION-V1: Parse structured error
      const structuredError = parseStructuredError(error);
      setExtractionError(structuredError);
      
      // Show brief toast with user message
      const userMessage = getUserFriendlyMessage(structuredError);
      toast.error(userMessage);
      
      // Log full details for developers
      console.error("[AI_EXTRACT] Full error details:", structuredError);
    } finally {
      setIsExtracting(false);
    }
  }, [documents]);
  
  // Validate extracted data
  const handleValidate = useCallback(async () => {
    setIsValidating(true);
    setValidationError(null); // Clear previous error
    
    try {
      const draft = extractionResult?.structured_draft || {};
      
      // Build extraction result with current form values
      const payload = {
        extraction_result: {
          first_name: { value: formData.first_name, confidence: draft.first_name?.confidence || 0.5, source: draft.first_name?.source },
          last_name: { value: formData.last_name, confidence: draft.last_name?.confidence || 0.5, source: draft.last_name?.source },
          date_of_birth: { value: formData.date_of_birth, confidence: draft.date_of_birth?.confidence || 0.5, source: draft.date_of_birth?.source },
          // EVENTSFLO-CREW-PLACE-OF-BIRTH-PROFILE-AND-AI-ONBOARDING-V1
          place_of_birth: { value: formData.place_of_birth, confidence: draft.place_of_birth?.confidence || 0.5, source: draft.place_of_birth?.source },
          nationality: { value: formData.nationality, confidence: draft.nationality?.confidence || 0.5, source: draft.nationality?.source },
          id_type: { value: formData.id_type, confidence: draft.id_type?.confidence || 0.5, source: draft.id_type?.source },
          id_number: { value: formData.id_number, confidence: draft.id_number?.confidence || 0.5, source: draft.id_number?.source },
          expiry_date: { value: formData.expiry_date, confidence: draft.expiry_date?.confidence || 0.5, source: draft.expiry_date?.source },
          phone: { value: formData.phone, confidence: draft.phone?.confidence || 0.5, source: draft.phone?.source },
          email: { value: formData.email, confidence: draft.email?.confidence || 0.5, source: draft.email?.source },
          address: { value: formData.address, confidence: draft.address?.confidence || 0.5, source: draft.address?.source },
          conflicts: extractionResult?.conflicts || [],
        },
      };
      
      const response = await api.post("/ai/crew/validate", payload, { timeout: 60000 }); // ONB-FIX-003: 60s ceiling
      
      // EF-AI-CREW-ONBOARDING-STABILISATION-V1: Check for structured error in response
      if (response.data && response.data.success === false) {
        const structuredError = parseStructuredError({ response: { data: response.data } });
        setValidationError(structuredError);
        console.error("[AI_VALIDATE] Structured error:", structuredError);
        return;
      }
      
      setValidationResult(response.data);
      
      if (response.data.is_valid) {
        toast.success("Validation passed - ready to create profile");
      } else {
        toast.warning("Validation issues detected - please review");
      }
    } catch (error) {
      console.error("Validation error:", error);
      
      // EF-AI-CREW-ONBOARDING-STABILISATION-V1: Parse structured error
      const structuredError = parseStructuredError(error);
      setValidationError(structuredError);
      
      const userMessage = getUserFriendlyMessage(structuredError);
      toast.error(userMessage);
    } finally {
      setIsValidating(false);
    }
  }, [extractionResult, formData]);
  
  // ONB-FIX-010 / ONB-FIX-013 / ONB-FIX-020: Shared helper — attaches the in-memory
  // onboarding documents to a staff profile (used by BOTH create and update flows).
  // EVERY uploaded file is attached: matched files use their seeded type, unmatched
  // files fall back to the generic "other" type so they are still preserved on the
  // crew profile (without satisfying any required-document checklist item).
  const attachDocumentsToProfile = useCallback(async (staffId) => {
    if (!staffId || documents.length === 0) {
      return { uploadedMatched: 0, uploadedOther: 0, failed: 0 };
    }

    let uploadedMatched = 0;
    let uploadedOther = 0;
    let failed = 0;

    try {
      // Idempotent: ensures the seeded types (including "other") exist before lookup.
      try {
        await api.post("/document-types/initialize");
      } catch (initErr) {
        // Non-fatal — continue with whatever types already exist.
        console.warn("[ONB] document-types/initialize warning:", initErr);
      }

      const dtRes = await api.get("/document-types");
      const docTypes = Array.isArray(dtRes.data) ? dtRes.data : [];
      const codeToId = {};
      docTypes.forEach((dt) => {
        if (dt && dt.code && dt.id) codeToId[dt.code] = dt.id;
      });

      // EVENTSFLO-CREW-INBOX-ONBOARDING-DOCUMENT-PERSISTENCE-HEALTH-EMAIL-FALLBACK-V1
      // Normalise extraction hints to internal codes. Match case-insensitively,
      // then fall back to filename heuristics so ID front/back never collide
      // and Carte Vitale / Monaco CCSS still classify when the AI returns
      // localised wording.
      const normaliseHint = (rawHint, filename) => {
        const h = String(rawHint || "").trim().toLowerCase();
        const fn = String(filename || "").toLowerCase();
        // Carte Vitale / French health
        if (h.includes("carte vitale") || h.includes("vitale") || h.includes("health card") || h.includes("health insurance") || fn.includes("vitale")) {
          return "carte_vitale";
        }
        // Monaco CCSS
        if (h.includes("ccss") || h.includes("monaco") || fn.includes("ccss")) {
          return "ccss_registration";
        }
        // Passport
        if (h.includes("passport") || fn.includes("passport")) return "passport";
        // CV / resume
        if (h === "cv" || h.includes("curriculum") || h.includes("résumé") || h.includes("resume") || fn.endsWith("-cv.pdf") || fn.endsWith("_cv.pdf")) {
          return "cv";
        }
        // Driver licence (EN / FR)
        // EVENTSFLO-CREW-ONBOARDING-ADDRESS-PROOF-AND-FRENCH-DOC-MAPPING-V1
        // French "permis" / "permis de conduire" must NEVER fall through to
        // `other`. Match both hint and filename, covering English variants
        // (driving licence, driver's license) and French (permis [de] conduire).
        const driverHintRe = /\b(driver(?:'s)?(?:\s+|-)?licen[cs]e|driving\s+licen[cs]e|permis(?:\s+(?:de\s+)?conduire)?)\b/;
        const driverFileRe = /(driver|driving[-_ ]?licen[cs]e|permis(?:[-_ ]?de)?[-_ ]?conduire|permis(?=[-_. ]))/;
        if (driverHintRe.test(h) || driverFileRe.test(fn) || h === "permis") {
          return "driver_license";
        }
        // Proof of address — wide net for EN/FR utility-bill labels.
        // EVENTSFLO-CREW-ONBOARDING-ADDRESS-PROOF-AND-FRENCH-DOC-MAPPING-V1
        // Mapping table (any match → proof_of_address):
        //   EN: proof of address | address proof | utility bill | electricity bill
        //   FR: justificatif (de domicile / adresse) | facture (EDF / électricité)
        //       attestation domicile | EDF | électricité | electricite
        const poaPatterns = [
          "proof of address", "address proof",
          "utility", "utility bill",
          "electricity bill", "electricity",
          "edf",
          "justificatif", "justificatif de domicile", "justificatif d'adresse", "justificatif adresse",
          "facture edf", "facture électricité", "facture electricite", "facture",
          "attestation domicile", "attestation de domicile",
          "domicile",
        ];
        if (poaPatterns.some((p) => h.includes(p)) ||
            /(?:^|[^a-z])(edf|justificatif|facture|domicile|attestation)/.test(fn) ||
            fn.includes("proof-of-address") || fn.includes("proof_of_address")) {
          return "proof_of_address";
        }
        // Bank details (RIB / IBAN / bank statement / account details)
        // EVENTSFLO-CREW-ONBOARDING-BANK-DETAILS-DOCUMENT-TYPE-V1
        // Mapping table (any match → bank_details):
        //   EN: bank details | bank account | iban | bic | swift |
        //       bank statement | account details
        //   FR: rib | relevé d'identité bancaire | coordonnées bancaires
        //   filenames: rib.* | iban.* | statement_*.* | bank_*.* | swift_*.*
        const bankHintPatterns = [
          "bank details", "bank account", "bank statement", "account details",
          "iban", "bic", "swift",
          "rib", "relevé d'identité bancaire", "releve d'identite bancaire",
          "coordonnées bancaires", "coordonnees bancaires",
        ];
        const bankFileRe = /(?:^|[^a-z])(rib|iban|bic|swift|bank|statement)(?:[^a-z]|$)/;
        if (bankHintPatterns.some((p) => h.includes(p)) ||
            bankFileRe.test(fn)) {
          return "bank_details";
        }
        // ID Card / Residence Permit — distinguish front vs back
        const looksLikeIdCard =
          h.includes("id card") ||
          h.includes("id back") ||
          h.includes("id front") ||
          h.includes("permit") ||
          h.includes("residence") ||
          h.includes("titre de séjour") ||
          h.includes("carte d'identit") ||
          h.includes("identity") ||
          fn.includes("permit") ||
          fn.includes("residence") ||
          fn.includes("ctn") ||
          fn.includes("idcard");
        if (looksLikeIdCard) {
          // Back-side hints
          const isBack =
            h.includes("back") ||
            h.includes("verso") ||
            h.includes(" rear") ||
            fn.includes("back") ||
            fn.includes("verso") ||
            fn.includes("-rear") ||
            fn.includes("_back") ||
            fn.includes("-back") ||
            /\bb\.(jpe?g|png|webp|pdf)$/i.test(filename || "") ||
            /[-_ ]2\.(jpe?g|png|webp|pdf)$/i.test(filename || "");
          return isBack ? "national_id_back" : "national_id_front";
        }
        return null; // caller falls back to "other"
      };

      // Track which slots we've already used so two docs with identical hints
      // don't collide on the same crew_documents (staff_id, document_type_id)
      // versioning slot. The second collision is auto-redirected:
      //   national_id_front → national_id_back
      //   <anything else>   → other
      const slotsUsed = new Set();

      const otherTypeId = codeToId["other"] || null;

      const toFile = (d) => {
        const b64 = d.file_content_base64 || "";
        const bin = atob(b64);
        const len = bin.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
        return new File([bytes], d.filename, {
          type: d.mime_type || "application/octet-stream",
        });
      };

      for (const d of documents) {
        // EVENTSFLO-CREW-ONBOARDING-TXT-RTF-TEXT-EXTRACTION-V1:
        // TXT / RTF attachments are extraction-only — they feed the AI
        // pipeline but MUST NOT be persisted to crew_documents.
        if (d.extract_only) {
          console.info("[ONB] Skipping extraction-only file (not persisted):", d.filename);
          continue;
        }
        let code = normaliseHint(d.document_type_hint, d.filename);
        // Re-route on collision so versioning rule doesn't supersede the
        // first file of the same type.
        if (code && slotsUsed.has(code)) {
          if (code === "national_id_front" && !slotsUsed.has("national_id_back")) {
            code = "national_id_back";
          } else {
            code = null; // forces "other" slot below
          }
        }
        let documentTypeId = code ? codeToId[code] : null;
        let isOther = false;
        if (!documentTypeId) {
          documentTypeId = otherTypeId;
          isOther = true;
          code = "other";
        }
        // The "other" slot can hold many docs simultaneously because the
        // backend versioning is per (staff_id, document_type_id). Two "other"
        // uploads will still collide on that slot, so for the SECOND+ "other"
        // upload we add a tiny suffix to the filename to make the version
        // visible and keep the file distinct on disk. (Backend creates a new
        // version row that supersedes the previous one — we want both files
        // physically uploaded; the versioning history retains both.)
        if (!documentTypeId) {
          // No matched type AND "other" not available — count as failed so the admin sees it.
          failed += 1;
          console.error("[ONB] No document type available (matched or 'other') for:", d.filename);
          continue;
        }
        slotsUsed.add(code);
        try {
          const fd = new FormData();
          fd.append("document_type_id", documentTypeId);
          fd.append("file", toFile(d));
          // ONB-FIX-029: Propagate the extracted ID expiry date to ID-document
          // uploads so the Crew Documents table Expiry column shows the value
          // the admin reviewed during onboarding.
          const idExpiry = (formData.expiry_date || "").trim();
          const isIdentityDoc =
            code === "national_id_front" ||
            code === "national_id_back" ||
            code === "passport";
          if (isIdentityDoc && idExpiry) {
            fd.append("expiry_date", idExpiry);
          }
          await api.post(`/staff/${staffId}/documents`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120000,
          });
          if (isOther) uploadedOther += 1;
          else uploadedMatched += 1;
        } catch (uploadErr) {
          console.error("[ONB] Document upload failed:", d.filename, uploadErr);
          failed += 1;
        }
      }
    } catch (docErr) {
      console.error("[ONB] Document attachment phase failed:", docErr);
    }

    // Distinct messaging for matched / unmatched-but-attached / failed.
    if (uploadedMatched > 0 && uploadedOther === 0 && failed === 0) {
      toast.success(`Attached ${uploadedMatched} document(s) to profile`);
    } else if (uploadedMatched > 0 && uploadedOther > 0 && failed === 0) {
      toast.success(
        `Attached ${uploadedMatched} matched + ${uploadedOther} as "Other" to profile`
      );
    } else if (uploadedMatched === 0 && uploadedOther > 0 && failed === 0) {
      toast.success(`Attached ${uploadedOther} document(s) as "Other" (no matching type)`);
    } else if ((uploadedMatched > 0 || uploadedOther > 0) && failed > 0) {
      toast.warning(
        `Attached ${uploadedMatched + uploadedOther} document(s); ${failed} failed`
      );
    } else if (failed > 0) {
      toast.error(`Failed to attach ${failed} document(s) to profile`);
    }

    return { uploadedMatched, uploadedOther, failed };
  }, [documents, formData.expiry_date]);

  // ONB-FIX-007 / ONB-FIX-015: Build the StaffCreate / StaffUpdate-shaped payload from formData.
  // Used by BOTH create and update paths so the user-reviewed data is the source of truth.
  // Country residency is DERIVED from extraction context (address_country first, then nationality),
  // never defaulted to United Kingdom.
  const buildStaffPayload = useCallback(() => {
    const toIsoDate = (raw) => {
      if (!raw) return null;
      const s = String(raw).trim();
      if (!s) return null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
      const m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
      if (m) {
        const dd = m[1].padStart(2, "0");
        const mm = m[2].padStart(2, "0");
        return `${m[3]}-${mm}-${dd}`;
      }
      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
    };

    // Derive country from the extraction context. NEVER default to "United Kingdom".
    // Preference order: explicit address_country -> nationality (best-effort) -> empty.
    const draft = extractionResult?.structured_draft || {};
    const extractedCountry = (draft.address_country?.value || "").toString().trim();
    const nationality = (formData.nationality || draft.nationality?.value || "")
      .toString()
      .trim();
    const nationalityToCountry = (nat) => {
      const n = nat.toLowerCase();
      // Best-effort minimal mapping; returns "" if uncertain.
      if (/fran[cç]ais|french|france/.test(n)) return "France";
      if (/british|british citizen|uk|united kingdom|england|english/.test(n))
        return "United Kingdom";
      if (/spani(?:sh|ard)|espagn|spain/.test(n)) return "Spain";
      if (/german|deutsch|germany/.test(n)) return "Germany";
      if (/italian|italy/.test(n)) return "Italy";
      if (/portuguese|portugal/.test(n)) return "Portugal";
      if (/belgian|belgium/.test(n)) return "Belgium";
      if (/swiss|switzerland/.test(n)) return "Switzerland";
      if (/dutch|netherlands/.test(n)) return "Netherlands";
      return "";
    };
    const derivedCountry = extractedCountry || nationalityToCountry(nationality);

    const addrText = (formData.address || "").trim();
    // EVENTSFLO-CREW-ONBOARDING-ADDRESS-PROOF-AND-FRENCH-DOC-MAPPING-V1
    const addrLine2 = (formData.address_line_2 || "").trim();
    const addrCity  = (formData.address_city || "").trim();
    const addrPostcode = (formData.address_postcode || "").trim();
    let address = null;
    if (addrText || addrLine2 || addrCity || addrPostcode || derivedCountry) {
      address = {
        line1: addrText || null,
        line2: addrLine2 || null,
        city: addrCity || null,
        postcode: addrPostcode || null,
        // StaffAddress.country is required (str) with a UK default — we MUST send it
        // explicitly so Pydantic does not silently fall back to "United Kingdom".
        country: derivedCountry || "",
      };
    }

    // EVENTSFLO-CREW-ONBOARDING-BANK-DETAILS-PERSIST-V1
    // Build bank_details only when the admin saw / typed at least one value;
    // sending an empty object would still be valid but adds noise. Maps:
    //   bank_account_holder → account_name (reused; existing UK field)
    //   bank_name           → bank_name
    //   bank_iban           → iban
    //   bank_bic_swift      → bic_swift
    //   bank_rib_key        → rib_key
    const bd = {
      account_name: (formData.bank_account_holder || "").trim() || null,
      bank_name:    (formData.bank_name || "").trim() || null,
      iban:         (formData.bank_iban || "").trim() || null,
      bic_swift:    (formData.bank_bic_swift || "").trim() || null,
      rib_key:      (formData.bank_rib_key || "").trim() || null,
    };
    const bankDetails = Object.values(bd).some(Boolean) ? bd : null;

    return {
      first_name: formData.first_name || null,
      last_name: formData.last_name || null,
      date_of_birth: toIsoDate(formData.date_of_birth),
      // EVENTSFLO-CREW-PLACE-OF-BIRTH-PROFILE-AND-AI-ONBOARDING-V1:
      // free-text city/town. Empty after trim → null so the backend stores
      // the explicit "cleared" state, not an empty string.
      place_of_birth: (formData.place_of_birth || "").trim() || null,
      nationality: nationality || null,
      phone: formData.phone || null,
      email: formData.email || null,
      address,
      // ONB-FIX-024: Persist extracted identity-document number to the staff record.
      // The Crew Profile "ID Number" field is bound to national_insurance_number; the
      // identity-document number from National ID / passport lives there.
      // (Carte Vitale's NIR is intentionally NOT placed here — it's already separated
      // into health_card_number on the document side.)
      national_insurance_number: (formData.id_number || "").trim() || null,
      // EVENTSFLO-CREW-ONBOARDING-BANK-DETAILS-PERSIST-V1
      ...(bankDetails ? { bank_details: bankDetails } : {}),
    };
  }, [formData, extractionResult]);

  // ONB-FIX-012: Try to locate an existing profile by email, then PATCH it
  // non-destructively with only the reviewed onboarding fields.
  const handleUpdateExistingProfile = useCallback(async () => {
    if (!existingProfileMatch?.email) {
      toast.error("No existing profile to update");
      return;
    }
    setIsUpdating(true);
    setCreateError(null);

    try {
      // Look up the existing staff record by email
      const searchRes = await api.get("/staff", {
        params: { search: existingProfileMatch.email, limit: 5 },
      });
      const items = Array.isArray(searchRes?.data?.items) ? searchRes.data.items : [];
      const existing = items.find(
        (s) =>
          (s.email || "").toLowerCase() === existingProfileMatch.email.toLowerCase()
      );

      if (!existing || !existing.id) {
        toast.error("Could not find the existing profile to update");
        return;
      }

      // ONB-FIX-012: Non-destructive PATCH — only send fields the admin actually
      // reviewed/changed. Empty/null reviewed values are NOT sent (so they cannot wipe
      // unrelated existing data on the profile).
      const reviewed = buildStaffPayload();
      const patchPayload = {};
      Object.entries(reviewed).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== "") {
          patchPayload[k] = v;
        }
      });

      await api.patch(`/staff/${existing.id}`, patchPayload);
      toast.success("Existing crew profile updated");

      // Attach onboarding documents to the existing profile too
      // ONB-FIX-016: surface attachment failures actionably.
      const attachResult = await attachDocumentsToProfile(existing.id);
      if (attachResult && attachResult.failed > 0) {
        setCreateError({
          isStructured: false,
          step: "ATTACH_DOCUMENTS",
          stepName: "Attach Onboarding Documents (Update Path)",
          errorType: "DocumentUploadError",
          message: `Profile updated, but ${attachResult.failed} document(s) failed to attach. The profile is saved; please retry or upload the failed files manually from the Crew Profile.`,
          debug: `staff_id=${existing.id}; matched=${attachResult.uploadedMatched}; other=${attachResult.uploadedOther}; failed=${attachResult.failed}`,
          fileName: null,
          traceId: null,
          timestamp: new Date().toISOString(),
        });
      }

      navigate(`/crew-management/${existing.id}/edit`);
    } catch (error) {
      console.error("Update profile error:", error);
      const friendlyMsg = extractErrorMessage(error);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      const debugLines = [];
      if (status) debugLines.push(`HTTP ${status}`);
      if (detail !== undefined) {
        try {
          debugLines.push(typeof detail === "string" ? detail : JSON.stringify(detail, null, 2));
        } catch {
          debugLines.push(String(detail));
        }
      }
      setCreateError({
        isStructured: false,
        step: "UPDATE_PROFILE",
        stepName: "Update Existing Profile",
        errorType: status ? `HTTP ${status}` : (error?.name || "Error"),
        message: friendlyMsg,
        debug: debugLines.join("\n"),
        fileName: null,
        traceId: null,
        timestamp: new Date().toISOString(),
      });
      toast.error(friendlyMsg);
    } finally {
      setIsUpdating(false);
    }
  }, [existingProfileMatch, buildStaffPayload, attachDocumentsToProfile, navigate]);

  // Create crew profile
  const handleCreateProfile = useCallback(async () => {
    // First validate
    if (!validationResult?.is_valid) {
      toast.error("Please resolve all validation issues first");
      return;
    }

    setIsCreating(true);
    setCreateError(null);
    setExistingProfileMatch(null);

    try {
      const payload = buildStaffPayload();
      const response = await api.post("/staff", payload);

      toast.success("Crew profile created successfully!");

      const newId = response.data?._id || response.data?.id;

      // EVENTSFLO-CREW-ID-PHOTO-DETECTION-AND-INBOX-DOWNLOAD-FIX-V1
      // If admin explicitly toggled "Use ID photo as profile photo" AND a
      // crop is available, upload it to the existing profile-photo endpoint.
      // Failure here NEVER blocks profile creation — we just toast a warning.
      if (
        newId
        && useIdPhotoAsProfile
        && extractionResult?.face_preview?.image_base64
      ) {
        try {
          const fp = extractionResult.face_preview;
          const mime = fp.mime_type || "image/png";
          const bin = atob(fp.image_base64);
          const arr = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
          const blob = new Blob([arr], { type: mime });
          const ext = mime === "image/jpeg" ? "jpg" : mime === "image/webp" ? "webp" : "png";
          const fd = new FormData();
          fd.append("file", blob, `profile.${ext}`);
          await api.post(`/staff/${newId}/photo`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.success("Profile photo set from ID document.");
        } catch (photoErr) {
          console.warn("[ONB] profile-photo upload failed:", photoErr);
          toast.warning("Profile created, but the ID photo could not be saved as the avatar. You can upload it manually from the crew profile.");
        }
      }

      if (newId && documents.length > 0) {
        const attachResult = await attachDocumentsToProfile(newId);
        if (attachResult && attachResult.failed > 0) {
          // ONB-FIX-016: surface attachment failures actionably (create path).
          setCreateError({
            isStructured: false,
            step: "ATTACH_DOCUMENTS",
            stepName: "Attach Onboarding Documents (Create Path)",
            errorType: "DocumentUploadError",
            message: `Profile created, but ${attachResult.failed} document(s) failed to attach. The profile is saved; please retry or upload the failed files manually from the Crew Profile.`,
            debug: `staff_id=${newId}; matched=${attachResult.uploadedMatched}; other=${attachResult.uploadedOther}; failed=${attachResult.failed}`,
            fileName: null,
            traceId: null,
            timestamp: new Date().toISOString(),
          });
        }
      }

      // EVENTSFLO-CREW-INBOX-ONBOARDING-HEALTH-RECORDS-AND-DISPLAY-NAME-V1
      // Persist any health records derived during AI extraction (Carte
      // Vitale → France, Monaco CCSS → Monaco, both can coexist).
      // Failures here NEVER block profile creation — they're surfaced as
      // a warning toast so the admin can add records manually if needed.
      if (newId) {
        const healthRecords = (extractionResult && Array.isArray(extractionResult.health_records))
          ? extractionResult.health_records
          : [];
        const merged = [...healthRecords];
        // Also pick up the manually edited Carte Vitale fields from the
        // form (covers the case where AI didn't return health_records but
        // the admin filled in health_card_type + number themselves).
        const ht = (formData.health_card_type || "").trim().toLowerCase();
        const hn = (formData.health_card_number || "").trim();
        const alreadyHasFrance = merged.some((r) => (r.system_type || "").toLowerCase() === "france");
        if (!alreadyHasFrance && (ht.includes("vitale") || (ht && hn))) {
          merged.push({
            system_type: "france",
            provider: "CPAM",
            insurance_number: hn || null,
            card_number: hn || null,
            raw_payload: { source: "form", health_card_type: ht || null },
          });
        }
        for (const rec of merged) {
          try {
            await api.post(`/staff/${newId}/health-records`, {
              system_type: rec.system_type,
              provider: rec.provider || null,
              insurance_number: rec.insurance_number || null,
              card_number: rec.card_number || null,
              valid_from: rec.valid_from || null,
              valid_to: rec.valid_to || null,
              raw_payload: rec.raw_payload || {},
            });
          } catch (hrErr) {
            console.warn("[ONB] Health record creation failed:", rec, hrErr);
            toast.warning(
              `Profile created, but a ${rec.system_type === "monaco" ? "Monaco" : "France"} health record could not be saved automatically. Add it manually from the Crew Profile.`,
              { duration: 6000 },
            );
          }
        }
      }

      if (newId) {
        navigate(`/crew-management/${newId}/edit`);
      } else {
        navigate("/crew-management");
      }
    } catch (error) {
      console.error("Create profile error:", error);

      const friendlyMsg = extractErrorMessage(error);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;

      // ONB-FIX-012: Detect duplicate-email conflict (DuplicateError → HTTP 409).
      // Surface an "Update Existing Profile" path instead of a generic failure.
      const detailStr = typeof detail === "string" ? detail : JSON.stringify(detail || "");
      const isDuplicate =
        status === 409 ||
        /already exists/i.test(detailStr) ||
        /duplicate/i.test(detailStr);

      if (isDuplicate && (formData.email || "").trim()) {
        setExistingProfileMatch({ email: formData.email.trim(), detail: detailStr });
      }

      const debugLines = [];
      if (status) debugLines.push(`HTTP ${status}`);
      if (detail !== undefined) {
        try {
          debugLines.push(typeof detail === "string" ? detail : JSON.stringify(detail, null, 2));
        } catch {
          debugLines.push(String(detail));
        }
      }

      setCreateError({
        isStructured: false,
        step: "CREATE_PROFILE",
        stepName: "Create Crew Profile",
        errorType: status ? `HTTP ${status}` : (error?.name || "Error"),
        message: friendlyMsg,
        debug: debugLines.join("\n"),
        fileName: null,
        traceId: null,
        timestamp: new Date().toISOString(),
      });

      toast.error(friendlyMsg);
    } finally {
      setIsCreating(false);
    }
  }, [formData.email, validationResult, navigate, documents, buildStaffPayload, attachDocumentsToProfile]);
  
  // Handle form field change
  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation when form changes
    setValidationResult(null);
  }, []);
  
  // Handle conflict resolution
  const handleResolveConflict = useCallback((field, newValue) => {
    handleFieldChange(field, newValue);
    // Remove the conflict from extraction result
    if (extractionResult?.conflicts) {
      setExtractionResult(prev => ({
        ...prev,
        conflicts: prev.conflicts.filter(c => c.field !== field),
      }));
    }
    toast.success(`Resolved conflict for ${field}`);
  }, [extractionResult, handleFieldChange]);
  
  // Get conflict for a field
  const getConflictForField = (fieldName) => {
    return extractionResult?.conflicts?.find(c => c.field === fieldName);
  };
  
  const selectedDoc = documents[selectedDocIndex];
  const draft = extractionResult?.structured_draft;
  
  return (
    <div className="h-full flex flex-col" data-testid="ai-crew-onboarding-page">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-background">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold">AI Crew Onboarding</h1>
            <p className="text-sm text-muted-foreground">
              Upload documents to extract crew profile data
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleExtract}
            disabled={documents.length === 0 || isExtracting}
            data-testid="btn-rerun-ai"
          >
            {isExtracting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {extractionResult ? "Re-run AI" : "Extract Data"}
          </Button>
          
          <Button
            variant="outline"
            onClick={handleValidate}
            disabled={!extractionResult || isValidating}
            data-testid="btn-validate"
          >
            {isValidating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            )}
            Validate Draft
          </Button>
          
          <Button
            onClick={handleCreateProfile}
            disabled={!validationResult?.is_valid || isCreating || isUpdating}
            data-testid="btn-create-profile"
          >
            {isCreating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <UserPlus className="h-4 w-4 mr-2" />
            )}
            Create Crew Profile
          </Button>

          {/* ONB-FIX-012: Update existing profile when create returned 409 */}
          {existingProfileMatch && (
            <Button
              variant="secondary"
              onClick={handleUpdateExistingProfile}
              disabled={isUpdating || isCreating}
              data-testid="btn-update-existing-profile"
            >
              {isUpdating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Update Existing Profile
            </Button>
          )}
        </div>
      </div>
      
      {/* Main content - Split screen */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT PANEL - Documents */}
        <div className="w-1/2 border-r flex flex-col">
          {/* Document list header */}
          <div className="p-4 border-b bg-muted/30">
            <div className="flex items-center justify-between">
              <h2 className="font-medium">Documents</h2>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Upload
                </Button>
              </div>
            </div>
          </div>
          
          {/* Document list */}
          <div
            className={`p-4 border-b transition-colors ${isDragging ? "bg-primary/5 ring-2 ring-primary/40 ring-inset" : ""}`}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            data-testid="dropzone-container"
          >
            {documents.length === 0 ? (
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragging
                    ? "border-primary bg-primary/10"
                    : "hover:border-primary/50"
                }`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                data-testid="dropzone"
              >
                <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm font-medium">
                  {isDragging ? "Release to upload" : "Drop documents here or click to upload"}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Supports PDF, JPG, PNG
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {documents.map((doc, idx) => (
                  <DocumentCard
                    key={idx}
                    doc={doc}
                    isSelected={idx === selectedDocIndex}
                    onSelect={() => setSelectedDocIndex(idx)}
                    onRemove={() => handleRemoveDocument(idx)}
                  />
                ))}
              </div>
            )}
          </div>
          
          {/* Document preview */}
          <div className="flex-1 p-4 overflow-auto bg-muted/10">
            <h3 className="text-sm font-medium mb-3">Preview</h3>
            {selectedDoc ? (
              <div className="rounded-lg border bg-white dark:bg-gray-900 overflow-hidden">
                {selectedDoc.mime_type?.startsWith("image/") ? (
                  <img
                    src={selectedDoc.preview_url}
                    alt={selectedDoc.filename}
                    className="max-w-full h-auto"
                    data-testid="document-preview-image"
                  />
                ) : selectedDoc.mime_type === "application/pdf" ? (
                  <PdfPreview
                    src={selectedDoc.preview_url}
                    fileName={selectedDoc.filename}
                  />
                ) : (selectedDoc.mime_type === "text/plain" ||
                     selectedDoc.mime_type === "text/rtf" ||
                     selectedDoc.mime_type === "application/rtf" ||
                     selectedDoc.filename?.toLowerCase().endsWith(".txt") ||
                     selectedDoc.filename?.toLowerCase().endsWith(".rtf")) ? (
                  /* EVENTSFLO-CREW-ONBOARDING-TXT-RTF-VIEWER-PREVIEW-V1 */
                  <TextPreview doc={selectedDoc} />
                ) : (
                  <div className="p-8 text-center">
                    <FileWarning className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm">Preview not available</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = selectedDoc.preview_url;
                        link.download = selectedDoc.filename;
                        link.click();
                      }}
                    >
                      Download File
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed p-8 text-center">
                <Eye className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Select a document to preview</p>
              </div>
            )}
          </div>
        </div>
        
        {/* RIGHT PANEL - Extracted Data Form */}
        <div className="w-1/2 flex flex-col overflow-hidden">
          <ScrollArea className="flex-1">
            <div className="p-4 space-y-6">
              {/* Extraction Status */}
              {!extractionResult && documents.length > 0 && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>Ready to Extract</AlertTitle>
                  <AlertDescription>
                    Click "Extract Data" to process {documents.length} document(s) with AI
                  </AlertDescription>
                </Alert>
              )}
              
              {isExtracting && (
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                      <div className="flex-1">
                        <p className="font-medium">Processing documents...</p>
                        <p className="text-sm text-muted-foreground">AI is extracting crew data</p>
                      </div>
                    </div>
                    <Progress value={33} className="mt-3" />
                  </CardContent>
                </Card>
              )}
              
              {/* EF-AI-CREW-ONBOARDING-STABILISATION-V1: Smart Error Panel */}
              {extractionError && (
                <SmartErrorPanel
                  error={extractionError}
                  onDismiss={() => setExtractionError(null)}
                />
              )}
              
              {/* Validation Error Panel */}
              {validationError && (
                <SmartErrorPanel
                  error={validationError}
                  onDismiss={() => setValidationError(null)}
                />
              )}

              {/* ONB-FIX-008: Create Profile Error Panel */}
              {createError && (
                <SmartErrorPanel
                  error={createError}
                  onDismiss={() => setCreateError(null)}
                />
              )}
              
              {/* Warnings Panel */}
              {extractionResult && (
                <WarningsPanel
                  warnings={extractionResult.warnings}
                  missingFields={validationResult?.missing_required}
                  lowConfidenceFields={validationResult?.low_confidence_fields}
                  conflicts={extractionResult.conflicts}
                />
              )}
              
              {/* Overall Confidence */}
              {extractionResult && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      Extraction Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <p className="text-sm text-muted-foreground">Overall Confidence</p>
                        <p className={`text-2xl font-bold ${getConfidenceColor(extractionResult.overall_confidence || 0)}`}>
                          {((extractionResult.overall_confidence || 0) * 100).toFixed(0)}%
                        </p>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-muted-foreground">Documents Processed</p>
                        <p className="text-2xl font-bold">{extractionResult.documents_processed?.length || 0}</p>
                      </div>
                      {extractionResult.conflicts?.length > 0 && (
                        <div className="flex-1">
                          <p className="text-sm text-muted-foreground">Conflicts</p>
                          <p className="text-2xl font-bold text-orange-600">{extractionResult.conflicts.length}</p>
                        </div>
                      )}
                    </div>
                    {extractionResult.is_draft && (
                      <Badge variant="outline" className="mt-3">
                        <Info className="h-3 w-3 mr-1" />
                        DRAFT - Review before saving
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* EVENTSFLO-CREW-ID-PHOTO-DETECTION-AND-INBOX-DOWNLOAD-FIX-V1
                  Cropped face preview from an ID/passport scan. Admin must
                  explicitly opt in via the toggle. The original document is
                  never used as the avatar — only this crop. */}
              {extractionResult?.face_preview?.image_base64 && (
                <Card data-testid="id-photo-suggestion-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <UserCircle className="h-4 w-4" />
                      ID Photo Detected
                    </CardTitle>
                    <CardDescription>
                      Use this cropped photo as the crew profile picture? The original document is never used as the avatar.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-start gap-4">
                      <div className="shrink-0">
                        <img
                          alt="Detected ID photo"
                          data-testid="id-photo-preview"
                          src={`data:${extractionResult.face_preview.mime_type || "image/png"};base64,${extractionResult.face_preview.image_base64}`}
                          className="w-24 h-24 rounded-lg object-cover border border-border bg-surface-elevated"
                        />
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex flex-wrap gap-2">
                          <Badge
                            variant={extractionResult.face_preview.quality === "high" ? "default" : "outline"}
                            className={
                              extractionResult.face_preview.quality === "high"
                                ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40"
                                : extractionResult.face_preview.quality === "medium"
                                  ? "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40"
                                  : "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40"
                            }
                          >
                            Quality: {extractionResult.face_preview.quality}
                          </Badge>
                          {extractionResult.face_preview.face_count > 1 && (
                            <Badge
                              variant="outline"
                              className="bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40"
                              title="More than one face detected. Please confirm the cropped photo above is the correct person."
                            >
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              {extractionResult.face_preview.face_count} faces detected
                            </Badge>
                          )}
                          {extractionResult.face_preview.source_document && (
                            <Badge variant="outline" className="text-muted-foreground">
                              Source: {extractionResult.face_preview.source_document}
                            </Badge>
                          )}
                        </div>
                        {extractionResult.face_preview.quality === "low" ? (
                          <p
                            className="text-xs text-amber-700 dark:text-amber-300"
                            data-testid="id-photo-low-quality-note"
                          >
                            <AlertTriangle className="inline h-3 w-3 mr-1" />
                            Detection quality is low — you may prefer manual crop. Toggle is disabled.
                          </p>
                        ) : (
                          <label className="flex items-center gap-2 cursor-pointer text-sm">
                            <input
                              type="checkbox"
                              data-testid="use-id-photo-toggle"
                              checked={!!useIdPhotoAsProfile}
                              onChange={(e) => setUseIdPhotoAsProfile(e.target.checked)}
                              className="h-4 w-4"
                            />
                            <span>Use ID photo as profile photo</span>
                          </label>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* EVENTSFLO-CREW-ID-PHOTO-DETECTION-FAILURE-UX-V1
                  When an identity-like upload was provided but the
                  detector found no usable face, surface a clear,
                  non-blocking notice directly above Identity. Admin can
                  dismiss with "Continue without photo". The crew profile
                  page always supports manual photo upload afterwards. */}
              {!extractionResult?.face_preview?.image_base64
                && extractionResult?.face_preview_status === "not_detected"
                && !idPhotoNoticeDismissed && (
                <Alert
                  data-testid="id-photo-not-detected-alert"
                  className="border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700"
                >
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertTitle className="text-amber-700 dark:text-amber-300">
                    ID photo not detected
                  </AlertTitle>
                  <AlertDescription className="text-amber-700 dark:text-amber-300 space-y-3">
                    <p>
                      We could not automatically detect a portrait in this document.
                      You can still complete onboarding and add a profile photo later.
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid="id-photo-continue-without"
                      className="border-amber-400 text-amber-700 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/30"
                      onClick={() => setIdPhotoNoticeDismissed(true)}
                    >
                      Continue without photo
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              {/* Identity Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Identity</CardTitle>
                  <CardDescription>Personal identification details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <ExtractedField
                      label="First Name"
                      field={draft?.first_name}
                      value={formData.first_name}
                      onChange={(v) => handleFieldChange("first_name", v)}
                      conflict={getConflictForField("first_name")}
                      onResolveConflict={(v) => handleResolveConflict("first_name", v)}
                    />
                    <ExtractedField
                      label="Last Name"
                      field={draft?.last_name}
                      value={formData.last_name}
                      onChange={(v) => handleFieldChange("last_name", v)}
                      conflict={getConflictForField("last_name")}
                      onResolveConflict={(v) => handleResolveConflict("last_name", v)}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <ExtractedField
                      label="Date of Birth"
                      field={draft?.date_of_birth}
                      value={formData.date_of_birth}
                      onChange={(v) => handleFieldChange("date_of_birth", v)}
                      conflict={getConflictForField("date_of_birth")}
                      onResolveConflict={(v) => handleResolveConflict("date_of_birth", v)}
                    />
                    <ExtractedField
                      label="Nationality"
                      field={draft?.nationality}
                      value={formData.nationality}
                      onChange={(v) => handleFieldChange("nationality", v)}
                      conflict={getConflictForField("nationality")}
                      onResolveConflict={(v) => handleResolveConflict("nationality", v)}
                    />
                  </div>
                  {/* EVENTSFLO-CREW-PLACE-OF-BIRTH-PROFILE-AND-AI-ONBOARDING-V1
                      Free-text Place of Birth extracted from "Lieu de
                      naissance" / "Place of birth" / "Né(e) à" / "Born in"
                      / "Birthplace" labels on the source document. Empty
                      string after trim is sent as null on save. */}
                  <div className="grid grid-cols-2 gap-4" data-testid="ai-onboarding-place-of-birth-row">
                    <ExtractedField
                      label="Place of Birth"
                      field={draft?.place_of_birth}
                      value={formData.place_of_birth}
                      onChange={(v) => handleFieldChange("place_of_birth", v)}
                      conflict={getConflictForField("place_of_birth")}
                      onResolveConflict={(v) => handleResolveConflict("place_of_birth", v)}
                    />
                  </div>
                </CardContent>
              </Card>
              
              {/* ID Document Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">ID Document</CardTitle>
                  <CardDescription>Identification document details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <ExtractedField
                      label="ID Type"
                      field={draft?.id_type}
                      value={formData.id_type}
                      onChange={(v) => handleFieldChange("id_type", v)}
                      conflict={getConflictForField("id_type")}
                      onResolveConflict={(v) => handleResolveConflict("id_type", v)}
                    />
                    <ExtractedField
                      label="ID Number"
                      field={draft?.id_number}
                      value={formData.id_number}
                      onChange={(v) => handleFieldChange("id_number", v)}
                      conflict={getConflictForField("id_number")}
                      onResolveConflict={(v) => handleResolveConflict("id_number", v)}
                    />
                  </div>
                  <ExtractedField
                    label="Expiry Date"
                    field={draft?.expiry_date}
                    value={formData.expiry_date}
                    onChange={(v) => handleFieldChange("expiry_date", v)}
                    conflict={getConflictForField("expiry_date")}
                    onResolveConflict={(v) => handleResolveConflict("expiry_date", v)}
                  />
                </CardContent>
              </Card>
              
              {/* Contact Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Contact</CardTitle>
                  <CardDescription>Contact information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <ExtractedField
                      label="Phone"
                      field={draft?.phone}
                      value={formData.phone}
                      onChange={(v) => handleFieldChange("phone", v)}
                      conflict={getConflictForField("phone")}
                      onResolveConflict={(v) => handleResolveConflict("phone", v)}
                    />
                    <ExtractedField
                      label="Email"
                      field={draft?.email}
                      value={formData.email}
                      onChange={(v) => handleFieldChange("email", v)}
                      conflict={getConflictForField("email")}
                      onResolveConflict={(v) => handleResolveConflict("email", v)}
                    />
                  </div>
                  {/* EVENTSFLO-CREW-INBOX-ONBOARDING-DOCUMENT-PERSISTENCE-HEALTH-EMAIL-FALLBACK-V1 */}
                  {formData.email_source === "email_sender" && formData.email && (
                    <p
                      className="text-xs text-amber-500 -mt-2"
                      data-testid="email-inferred-from-sender-banner"
                    >
                      Email inferred from sender. Edit if you have a better contact email.
                    </p>
                  )}
                  <ExtractedField
                    label="Address"
                    field={draft?.address}
                    value={formData.address}
                    onChange={(v) => handleFieldChange("address", v)}
                    conflict={getConflictForField("address")}
                    onResolveConflict={(v) => handleResolveConflict("address", v)}
                  />
                  {/* EVENTSFLO-CREW-ONBOARDING-ADDRESS-PROOF-AND-FRENCH-DOC-MAPPING-V1
                      Surface the structured address fields (line 2 / city /
                      postcode) so they round-trip through the form to
                      StaffAddress.line2 / .city / .postcode. */}
                  <ExtractedField
                    label="Address Line 2 (apartment / building)"
                    field={draft?.address_line_2}
                    value={formData.address_line_2}
                    onChange={(v) => handleFieldChange("address_line_2", v)}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <ExtractedField
                      label="City"
                      field={draft?.address_city}
                      value={formData.address_city}
                      onChange={(v) => handleFieldChange("address_city", v)}
                    />
                    <ExtractedField
                      label="Postcode"
                      field={draft?.address_postal_code}
                      value={formData.address_postcode}
                      onChange={(v) => handleFieldChange("address_postcode", v)}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* ONB-FIX-011: Health Insurance Section — separate from Identity */}
              <Card data-testid="health-insurance-section">
                <CardHeader>
                  <CardTitle className="text-base">Health Insurance</CardTitle>
                  <CardDescription>
                    Carte Vitale / health-card data, kept separate from identity-document fields
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <ExtractedField
                      label="Health Card Type"
                      field={draft?.health_card_type}
                      value={formData.health_card_type}
                      onChange={(v) => handleFieldChange("health_card_type", v)}
                    />
                    <ExtractedField
                      label="Health Card / NIR Number"
                      field={draft?.health_card_number}
                      value={formData.health_card_number}
                      onChange={(v) => handleFieldChange("health_card_number", v)}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* EVENTSFLO-CREW-ONBOARDING-BANK-DETAILS-FRONTEND-V1
                  Bank Details (RIB) section. Renders only when at least one
                  bank_details field has a value (admin saw something extracted)
                  OR when the admin starts typing — avoids showing an empty,
                  confusing card when no RIB was attached. */}
              {(() => {
                const bd = draft?.bank_details || {};
                const anyExtracted = !!(
                  bd.account_holder?.value || bd.bank_name?.value ||
                  bd.iban?.value || bd.bic_swift?.value || bd.rib_key?.value
                );
                const anyTyped = !!(
                  formData.bank_account_holder || formData.bank_name ||
                  formData.bank_iban || formData.bank_bic_swift || formData.bank_rib_key
                );
                if (!anyExtracted && !anyTyped) return null;
                return (
                  <Card data-testid="bank-details-section">
                    <CardHeader>
                      <CardTitle className="text-base">Employment · Bank Details (RIB)</CardTitle>
                      <CardDescription>
                        Extracted from RIB / bank-account confirmation. Review carefully and edit before save.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <ExtractedField
                          label="Account holder"
                          field={bd.account_holder}
                          value={formData.bank_account_holder}
                          onChange={(v) => handleFieldChange("bank_account_holder", v)}
                          data-testid="bank-account-holder-input"
                        />
                        <ExtractedField
                          label="Bank name"
                          field={bd.bank_name}
                          value={formData.bank_name}
                          onChange={(v) => handleFieldChange("bank_name", v)}
                          data-testid="bank-name-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <ExtractedField
                          label="IBAN"
                          field={bd.iban}
                          value={formData.bank_iban}
                          onChange={(v) => handleFieldChange("bank_iban", v)}
                          data-testid="bank-iban-input"
                        />
                        <ExtractedField
                          label="BIC / SWIFT"
                          field={bd.bic_swift}
                          value={formData.bank_bic_swift}
                          onChange={(v) => handleFieldChange("bank_bic_swift", v)}
                          data-testid="bank-bic-swift-input"
                        />
                      </div>
                      <ExtractedField
                        label="RIB key (legacy domestic format)"
                        field={bd.rib_key}
                        value={formData.bank_rib_key}
                        onChange={(v) => handleFieldChange("bank_rib_key", v)}
                        data-testid="bank-rib-key-input"
                      />
                    </CardContent>
                  </Card>
                );
              })()}
              
              {/* Certifications Section (Read-only for now) */}
              {draft?.certifications?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Certifications</CardTitle>
                    <CardDescription>Detected certifications from documents</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {draft.certifications.map((cert, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 rounded border">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <span className="text-sm">{cert.value}</span>
                          {cert.confidence && (
                            <Badge variant="outline" className={getConfidenceColor(cert.confidence)}>
                              {(cert.confidence * 100).toFixed(0)}%
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}
